import {
  Button,
  Card,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import HeadingWithDescription from '../../components/headingWithDescription';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import ActivityIndicator from '../../components/activityIndicator';
import { AppointmentResponse } from '../../types/appointment';
import AppointmentServices from '../../services/appointment.services';
import { CheckCircle } from '@mui/icons-material';
import moment from 'moment';

const SLOTS = [
  {
    slotName: '9:00 AM - 01:00 PM',
    slotCode: '01',
  },
  {
    slotName: '01:00 PM - 05:00 PM',
    slotCode: '02',
  },
  {
    slotName: '05:00 PM - 08:00 PM',
    slotCode: '03',
  },
];

function SingleRow({
  title,
  value,
  shouldHighlight = false,
}: {
  title: string | undefined;
  value: string | undefined;
  shouldHighlight?: boolean;
}) {
  return (
    <TableRow>
      <TableCell>{title}</TableCell>
      <TableCell sx={{ fontWeight: shouldHighlight ? '600' : '400' }} align="right">
        {value}
      </TableCell>
    </TableRow>
  );
}

function ReScheduleModal({
  open,
  handleClose,
  appointmentDetails,
  setAppointmentDetails,
  handleConfirm,
  handleCancel,
}: {
  open: boolean;
  handleClose: any;
  appointmentDetails: AppointmentResponse;
  setAppointmentDetails: any;
  handleConfirm: any;
  handleCancel: any;
}) {
  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    height: 'max-content',
    boxShadow: 24,
  };

  const currentHour = dayjs().get('hour');

  const isSlotDisabled = (date: any, slot: string) => {
    let isSameDate = dayjs().isSame(date, 'date');

    if (isSameDate) {
      if (currentHour >= 9 && slot === '01') {
        return true;
      }
      if (currentHour >= 13 && slot === '02') {
        return true;
      }
      if (currentHour >= 17 && slot === '03') {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    let toSetDate = dayjs(
      appointmentDetails?.tech_appointment === '1900-01-01'
        ? new Date()
        : appointmentDetails?.tech_appointment,
    );
    let toSetSlot = appointmentDetails?.tech_slot ?? '01';
    let isSameDate = dayjs().isSame(toSetDate, 'date');
    let isAfterDate = dayjs().isAfter(toSetDate, 'date');

    if (isSameDate) {
      toSetDate = toSetDate.add(1, 'D');
    }

    if (isAfterDate) {
      toSetDate = dayjs(moment(new Date()).add('d', 1).toDate());
    }

    open &&
      setAppointmentDetails({
        ...appointmentDetails,
        technician_appointment: toSetDate.format('YYYY-MM-DD'),
        tech_slot: toSetSlot,
      });
    // eslint-disable-next-line
  }, [open]);

  return (
    <Modal sx={modalStyle} open={open} onClose={handleClose}>
      <Stack sx={{ bgcolor: 'white' }} p={4} height={'100%'} gap={2}>
        <Stack bgcolor={'white'} p={2} gap={2}>
          <Typography variant="h5">Re-Schedule</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="YYYY-MM-DD"
              sx={{ width: '100%' }}
              label="SELECT DATE"
              value={
                dayjs(
                  appointmentDetails?.tech_appointment !== '1900-01-01'
                    ? appointmentDetails?.tech_appointment
                    : new Date(),
                ) as any
              }
              minDate={dayjs(new Date()).add(1, 'day')}
              maxDate={dayjs().add(7, 'day')}
              onChange={(newValue) =>
                setAppointmentDetails({
                  ...appointmentDetails,
                  tech_appointment: dayjs(newValue).format('YYYY-MM-DD'),
                })
              }
            />
          </LocalizationProvider>
          <FormControl fullWidth>
            <InputLabel id="slot-select-label">Select Slot</InputLabel>
            <Select
              labelId="slot-select-label"
              id="slot-select"
              value={appointmentDetails?.tech_slot}
              label="Select Slot"
              onChange={(e) =>
                setAppointmentDetails({
                  ...appointmentDetails,
                  tech_slot: e.target.value,
                })
              }
            >
              {SLOTS.map((s) => {
                return (
                  <MenuItem
                    key={s.slotCode}
                    disabled={isSlotDisabled(appointmentDetails?.tech_appointment, s.slotName)}
                    value={s.slotCode}
                  >
                    {s.slotName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Stack direction={'row'} gap={2}>
            <Button
              disabled={appointmentDetails?.tech_appointment === '1900-01-01'}
              onClick={() => handleConfirm(false)}
              fullWidth
              variant="contained"
            >
              Confirm
            </Button>
            <Button onClick={handleCancel} fullWidth color="secondary" variant="contained">
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
}

const isPastAppointmentTime = (date: string): boolean => {
  let todaysDate = moment(new Date()).format('YYYY-MM-DD');
  let appointmentDate = moment(date);
  if (moment('1900-01-01').isSame(moment(appointmentDate))) {
    return false;
  }

  if (appointmentDate.isAfter(todaysDate)) {
    return false;
  }

  if (appointmentDate.isSame(todaysDate)) {
    return false;
  }

  return true;
};

export default function Appointment() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const appointmentNumber = searchParams.get('id');
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentResponse | null>(null);
  const [prevappointmentDetails, setPrevAppointmentDetails] = useState<AppointmentResponse | null>(
    null,
  );
  const [hasTechCalled, setHasTechCalled] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [selectedOption, setSelectedOption] = useState<
    'Confirmed' | 'Reschedule' | 'Technician not called'
  >('Confirmed');

  const getAppointmentDetails = async (appointmentNumber: string) => {
    try {
      const res = await AppointmentServices.getAppointmentDetails(appointmentNumber);
      if (res) {
        setAppointmentDetails(res);
        setPrevAppointmentDetails(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setMessage('');
    setError('');
    try {
      if (appointmentDetails) {
        const res = await AppointmentServices.confirmAppointment(
          appointmentDetails?.ticket_id,
          appointmentDetails?.mobile,
          appointmentDetails?.tech_appointment,
          appointmentDetails?.tech_slot,
          selectedOption === 'Confirmed',
          hasTechCalled,
        );
        if (res) {
          toast.success('Schedule confirmed');
          setMessage('Visit scheduled successfully');
          setEditing(false);
        } else {
          toast.error('Something went wrong');
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setAppointmentDetails(prevappointmentDetails);
    setEditing(false);
  };

  useEffect(() => {
    setHasTechCalled(selectedOption === 'Technician not called');

    if (selectedOption !== 'Confirmed') {
      const isSameDayOrPast = moment(appointmentDetails?.tech_appointment).isSameOrBefore(
        moment(new Date()),
        'day',
      );

      if (isSameDayOrPast) {
        setError(
          'Due to the unavailability of slots, rescheduling is not possible at this moment. please call our customer care 08045845678 / 08069545678.',
        );
        setSelectedOption('Confirmed');
      } else {
        setEditing(true);
      }
    }

    // eslint-disable-next-line
  }, [selectedOption]);

  useEffect(() => {
    if (appointmentNumber) {
      getAppointmentDetails(appointmentNumber);
    }
    // eslint-disable-next-line
  }, [appointmentNumber]);

  if (!appointmentNumber) return <Navigate to="/" />;

  return (
    <>
      {loading && <ActivityIndicator />}
      <Stack p={2} bgcolor={'#f7f7f7'} flex={1} gap={2}>
        <HeadingWithDescription
          heading="Schedule Technician Visit"
          description="Confirm or Re-schedule your service visit"
        />
        {appointmentDetails && (
          <TableContainer sx={{ bgcolor: 'white' }} component={Paper}>
            <Table>
              <TableBody>
                <SingleRow
                  title="Ticket ID"
                  value={appointmentDetails?.ticket_id}
                  shouldHighlight
                />
                <SingleRow title="Mobile" value={appointmentDetails?.mobile as any} />
                <SingleRow
                  title="Date"
                  value={
                    (!appointmentDetails?.tech_appointment
                      ? '-'
                      : appointmentDetails?.tech_appointment !== '1900-01-01'
                      ? dayjs(appointmentDetails?.tech_appointment).format('YYYY-MM-DD')
                      : '-') as string
                  }
                />
                <SingleRow
                  title="Slot"
                  value={
                    appointmentDetails?.tech_slot
                      ? SLOTS.find((s) => s.slotCode === appointmentDetails?.tech_slot)?.slotName ??
                        SLOTS[0].slotName
                      : '-'
                  }
                />
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {appointmentDetails &&
        !message?.length &&
        !isPastAppointmentTime(appointmentDetails?.tech_appointment) ? (
          <>
            <FormControl sx={{ px: 1 }}>
              <RadioGroup
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value as any)}
              >
                <FormControlLabel
                  value="Confirmed"
                  control={<Radio size="small" />}
                  label="Ok, confirmed."
                />
                <FormControlLabel
                  value="Technician not called"
                  control={<Radio size="small" />}
                  label="Technician did not call, please reschedule."
                />
                <FormControlLabel
                  value="Reschedule"
                  control={<Radio size="small" />}
                  label="I want to reschedule."
                />
              </RadioGroup>
            </FormControl>
            <Stack direction={'row'} gap={2}>
              {
                <Button
                  disabled={prevappointmentDetails?.tech_appointment === '1900-01-01'}
                  onClick={handleConfirm}
                  fullWidth
                  variant="contained"
                  color="success"
                >
                  Submit
                </Button>
              }
            </Stack>
            <ReScheduleModal
              open={editing}
              handleClose={() => setEditing(false)}
              handleConfirm={handleConfirm}
              appointmentDetails={appointmentDetails}
              setAppointmentDetails={setAppointmentDetails}
              handleCancel={handleCancel}
            />
          </>
        ) : message?.length ? (
          <Card variant="outlined">
            <Stack direction={'row'} p={2} gap={1} alignItems={'center'}>
              <CheckCircle color="success" />
              <Typography variant="body2">{message}.</Typography>
            </Stack>
          </Card>
        ) : null}
        <Stack>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
}
